/**
 * AttachmentViewer — the ONE in-app preview for task (and comment)
 * attachments, shared by web peek + mobile task detail (#1535).
 *
 * Supersedes: task-detail-peek.tsx's AttachmentLightbox (image + blind-
 * iframe-for-everything-else, no video/other handling) and primitives.tsx's
 * PhotoLightbox (image-only placeholder explicitly marked as superseded by
 * this issue). Never navigates out of the app or hands a raw URL to the
 * browser — every attachment type gets an in-app render or an explicit
 * Download action.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Download, File as FileIcon, X } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { Spinner } from '@/components/atoms'
import { getToken } from '@/lib/api'
import {
  attachmentName, attachmentUrl, fileKindOf, formatFileSize, withSessionToken, type Attachment,
} from './attachments'

export interface ViewerAttachment {
  /** Already-absolutized (apiBaseUrl-resolved) URL. */
  url: string
  filename: string
  mimeType?: string
  size?: number
}

/** Adapts the shared mobile `Attachment` shape into the viewer's generic props. */
export function toViewerAttachment(a: Attachment): ViewerAttachment {
  return {
    url: attachmentUrl(a), filename: attachmentName(a), mimeType: a.content_type, size: a.file_size,
  }
}

// Portals to document.body, so this needs to out-rank every other overlay it
// can be opened from (SidePeek maxes at z-index 300, mobile sheets/nav at
// ~300) regardless of stacking context. Matches the z-index the lightbox
// this component supersedes already used.
const VIEWER_Z_INDEX = 2147483000

const MIN_ZOOM = 1
const MAX_ZOOM = 6

const toolbarBtn: React.CSSProperties = {
  background: 'var(--scrim-btn-bg)', border: 'none', cursor: 'pointer',
  color: 'var(--text-on-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
}

function isNativeIOS(): boolean {
  try { return Capacitor.getPlatform() === 'ios' } catch { return false }
}

// iOS app: PDFs open in the system viewer (SFSafariViewController via
// @capacitor/browser), not the in-page <iframe>. The WebView's origin is
// capacitor://localhost, so an iframe of app.usetenon.com/uploads/... is a
// cross-origin frame that frame-ancestors refuses (blank white box) — and
// even when WKWebView does frame a PDF it paints only page 1 with no
// scroll/zoom, useless for multi-page shop drawings. The Safari sheet is
// in-app (a modal over Tenon, Done returns), renders every page with
// pinch-zoom + share, and authenticates via ?session_token= (no cookie).
// Exported for tests.
export function nativePdfUrl(url: string): string {
  return withSessionToken(url, getToken())
}

function dist(a: React.Touch, b: React.Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

export function AttachmentViewer({
  attachment, onClose,
}: {
  attachment: ViewerAttachment | null
  onClose: () => void
}) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const pinch = useRef<{ dist: number; zoom: number } | null>(null)

  // Reset per-attachment state — keyed on url so re-opening the same
  // attachment (or switching between two) always starts from a clean state.
  useEffect(() => {
    setStatus('loading')
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [attachment?.url])

  // iframe `onLoad` fires even for a 404/500 error page — it can't tell us
  // the PDF actually failed. HEAD-check up front so a broken PDF still hits
  // the broken state instead of silently rendering the server's error page.
  useEffect(() => {
    if (!attachment || isNativeIOS() || fileKindOf(attachment.filename, attachment.mimeType) !== 'pdf') return
    let cancelled = false
    fetch(attachment.url, { method: 'HEAD' })
      .then(r => { if (!cancelled && !r.ok) setStatus('error') })
      .catch(() => { if (!cancelled) setStatus('error') })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.url, attachment?.mimeType, attachment?.filename])

  const nativePdf = !!attachment && isNativeIOS() &&
    fileKindOf(attachment.filename, attachment.mimeType) === 'pdf'
  useEffect(() => {
    if (!nativePdf || !attachment) return
    Browser.open({ url: nativePdfUrl(attachment.url) }).catch(() => {})
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativePdf, attachment?.url])

  useEffect(() => {
    if (!attachment) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [attachment, onClose])

  if (!attachment || nativePdf) return null
  const kind = fileKindOf(attachment.filename, attachment.mimeType)
  const isImg = kind === 'image'
  const sizeLabel = formatFileSize(attachment.size)

  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }) }
  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))

  const onWheel: React.WheelEventHandler = (e) => {
    if (!isImg) return
    e.preventDefault()
    setZoom(z => {
      const next = clampZoom(z - e.deltaY * 0.0015)
      if (next <= 1) setPan({ x: 0, y: 0 })
      return next
    })
  }

  const onMouseDown: React.MouseEventHandler = (e) => {
    if (!isImg || zoom <= 1) return
    drag.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
  }
  const onMouseMove: React.MouseEventHandler = (e) => {
    if (!drag.current) return
    setPan({ x: drag.current.panX + (e.clientX - drag.current.x), y: drag.current.panY + (e.clientY - drag.current.y) })
  }
  const endDrag = () => { drag.current = null }

  const onTouchStart: React.TouchEventHandler = (e) => {
    if (!isImg) return
    if (e.touches.length === 2) {
      pinch.current = { dist: dist(e.touches[0], e.touches[1]), zoom }
    } else if (e.touches.length === 1 && zoom > 1) {
      drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, panX: pan.x, panY: pan.y }
    }
  }
  const onTouchMove: React.TouchEventHandler = (e) => {
    if (!isImg) return
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault()
      const ratio = dist(e.touches[0], e.touches[1]) / (pinch.current.dist || 1)
      const next = clampZoom(pinch.current.zoom * ratio)
      setZoom(next)
      if (next <= 1) setPan({ x: 0, y: 0 })
    } else if (e.touches.length === 1 && drag.current) {
      e.preventDefault()
      const t = e.touches[0]
      setPan({ x: drag.current.panX + (t.clientX - drag.current.x), y: drag.current.panY + (t.clientY - drag.current.y) })
    }
  }
  const onTouchEnd: React.TouchEventHandler = (e) => {
    if (e.touches.length < 2) pinch.current = null
    if (e.touches.length < 1) drag.current = null
  }

  return createPortal(
    <div
      onClick={onClose}
      onWheel={onWheel}
      style={{
        position: 'fixed', inset: 0, zIndex: VIEWER_Z_INDEX,
        background: 'rgba(0,0,0,0.9)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Top bar */}
      <div onClick={(e) => e.stopPropagation()} style={{
        position: 'relative', zIndex: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', background: 'linear-gradient(rgba(0,0,0,0.55), transparent)',
      }}>
        <span style={{
          flex: 1, minWidth: 0, color: 'var(--text-on-scrim)', fontFamily: 'var(--font-sans)',
          fontSize: 13, fontWeight: 600, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{attachment.filename}</span>
        {isImg && status === 'loaded' && (
          <>
            <button type="button" onClick={() => setZoom(z => { const n = clampZoom(z - 0.25); if (n <= 1) setPan({ x: 0, y: 0 }); return n })}
              aria-label="Zoom out" style={toolbarBtn}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>−</span>
            </button>
            <span style={{ color: 'var(--text-on-scrim)', fontSize: 12, fontFamily: 'var(--font-mono)', minWidth: 44, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button type="button" onClick={() => setZoom(z => clampZoom(z + 0.25))} aria-label="Zoom in" style={toolbarBtn}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            </button>
            <button type="button" onClick={resetZoom} aria-label="Reset zoom"
              style={{ ...toolbarBtn, width: 'auto', padding: '0 10px', fontSize: 12 }}>Reset</button>
          </>
        )}
        <a href={attachment.url} download={attachment.filename}
          onClick={(e) => e.stopPropagation()}
          style={{ ...toolbarBtn, color: 'var(--text-on-scrim)', textDecoration: 'none' }}
          title="Download" aria-label="Download">
          <Download size={16} strokeWidth={2}/>
        </a>
        <button type="button" onClick={onClose} aria-label="Close preview" style={toolbarBtn}>
          <X size={18} strokeWidth={2}/>
        </button>
      </div>

      {/* Body */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, padding: kind === 'other' ? 0 : '0 12px 24px' }}
      >
        {status === 'loading' && kind !== 'other' && (
          <div style={{ position: 'absolute' }}>
            <Spinner size="lg" color="var(--text-on-scrim)"/>
          </div>
        )}
        {status === 'error' && (
          <BrokenState attachment={attachment}/>
        )}
        {kind === 'image' && status !== 'error' && (
          <img
            src={attachment.url}
            alt={attachment.filename}
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('error')}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={onMouseDown}
            draggable={false}
            style={{
              maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain',
              opacity: status === 'loaded' ? 1 : 0,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: drag.current ? 'none' : 'transform 80ms ease-out, opacity 150ms ease-out',
              cursor: zoom > 1 ? 'grab' : 'zoom-in', userSelect: 'none', touchAction: 'none',
            }}
          />
        )}
        {kind === 'pdf' && status !== 'error' && (
          <iframe
            src={attachment.url}
            title={attachment.filename}
            onLoad={() => setStatus('loaded')}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90vw', height: '88vh', border: 'none', borderRadius: 8,
              background: 'var(--scrim-content-bg)',
              opacity: status === 'loaded' ? 1 : 0, transition: 'opacity 150ms ease-out',
            }}
          />
        )}
        {kind === 'video' && status !== 'error' && (
          <video
            src={attachment.url}
            controls
            autoPlay
            onLoadedData={() => setStatus('loaded')}
            onError={() => setStatus('error')}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '88vh', borderRadius: 8,
              opacity: status === 'loaded' ? 1 : 0, transition: 'opacity 150ms ease-out',
            }}
          >
            Your browser does not support video playback.
          </video>
        )}
        {kind === 'other' && (
          <OtherFileState attachment={attachment} sizeLabel={sizeLabel}/>
        )}
      </div>
    </div>,
    document.body,
  )
}

function BrokenState({ attachment }: { attachment: ViewerAttachment }) {
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', color: 'var(--text-on-scrim)' }}>
      <FileIcon size={48} strokeWidth={1.6} style={{ opacity: 0.7, marginBottom: 12 }}/>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        Couldn't load
      </div>
      <a href={attachment.url} download={attachment.filename} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
        padding: '9px 16px', borderRadius: 8, background: 'var(--brand-gold)', color: 'var(--brand-gold-ink)',
        textDecoration: 'none', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
      }}>
        <Download size={14} strokeWidth={2.2}/> Download
      </a>
    </div>
  )
}

function OtherFileState({ attachment, sizeLabel }: { attachment: ViewerAttachment; sizeLabel: string | null }) {
  return (
    <div onClick={(e) => e.stopPropagation()} style={{
      textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 16,
      padding: '40px 32px', maxWidth: '85vw',
    }}>
      <FileIcon size={44} strokeWidth={1.6} style={{ color: 'var(--text-muted)', marginBottom: 16 }}/>
      <div style={{
        fontFamily: 'var(--font-sans)', fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)',
        wordBreak: 'break-word', marginBottom: 4,
      }}>{attachment.filename}</div>
      {sizeLabel && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          {sizeLabel}
        </div>
      )}
      <a href={attachment.url} download={attachment.filename} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: sizeLabel ? 0 : 16,
        padding: '10px 20px', borderRadius: 8, background: 'var(--brand-gold)', color: 'var(--brand-gold-ink)',
        textDecoration: 'none', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
      }}>
        <Download size={14} strokeWidth={2.2}/> Download
      </a>
    </div>
  )
}
